// CMS Context for Authentication and Theme
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CMSContext = createContext(null);

export const useCMS = () => {
  const context = useContext(CMSContext);
  if (!context) {
    throw new Error('useCMS must be used within CMSProvider');
  }
  return context;
};

export const CMSProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('cms_token'));
  const [loading, setLoading] = useState(true);
  
  // Theme state - default to dark, persist in localStorage
  const [cmsTheme, setCmsTheme] = useState(() => {
    const stored = localStorage.getItem('cms_theme');
    return stored || 'dark';
  });

  useEffect(() => {
    if (token) {
      verifyToken();
    } else {
      setLoading(false);
    }
  }, []);

  // Apply theme class to document when theme changes
  useEffect(() => {
    localStorage.setItem('cms_theme', cmsTheme);
  }, [cmsTheme]);

  const toggleCmsTheme = () => {
    setCmsTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const verifyToken = async () => {
    try {
      const response = await axios.get(`${API}/cms/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/cms/auth/login`, { email, password });
    const { token: newToken, admin: adminData } = response.data;
    localStorage.setItem('cms_token', newToken);
    setToken(newToken);
    setAdmin(adminData);
    return adminData;
  };

  const logout = () => {
    localStorage.removeItem('cms_token');
    setToken(null);
    setAdmin(null);
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const hasRole = (...roles) => {
    return admin && roles.includes(admin.role);
  };

  return (
    <CMSContext.Provider value={{
      admin,
      token,
      loading,
      login,
      logout,
      getAuthHeaders,
      hasRole,
      isAuthenticated: !!admin,
      cmsTheme,
      setCmsTheme,
      toggleCmsTheme
    }}>
      {children}
    </CMSContext.Provider>
  );
};

export default CMSContext;
